// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Deployment 25 — pagina dinamica per le guide DB-driven.
// Render minimal: H2 (## ), elenchi puntati (- ), citazioni (*), link [testo](url).
const renderInline = (text) => {
  // [text](url) → React link
  const parts = [];
  let lastIdx = 0;
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <Link key={`l-${key++}`} to={m[2]} className="text-[#C9A84C] underline hover:opacity-80">
        {m[1]}
      </Link>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  // Bold **text**
  return parts.map((p, i) => {
    if (typeof p !== 'string') return p;
    const out = [];
    const re = /\*\*([^*]+)\*\*/g;
    let li = 0;
    let mm;
    let k = 0;
    while ((mm = re.exec(p)) !== null) {
      if (mm.index > li) out.push(p.slice(li, mm.index));
      out.push(<strong key={`b-${i}-${k++}`}>{mm[1]}</strong>);
      li = mm.index + mm[0].length;
    }
    if (li < p.length) out.push(p.slice(li));
    return <React.Fragment key={`f-${i}`}>{out}</React.Fragment>;
  });
};

const renderContent = (content) => {
  const lines = content.split('\n');
  const blocks = [];
  let listBuffer = [];
  let key = 0;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    blocks.push(
      <ul key={`ul-${key++}`} className="list-disc pl-6 space-y-2 my-4 text-[#E8E8E4]">
        {listBuffer.map((l, i) => <li key={i}>{renderInline(l)}</li>)}
      </ul>
    );
    listBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith('## ')) {
      flushList();
      blocks.push(
        <h2 key={`h2-${key++}`} className="text-2xl sm:text-3xl font-semibold text-[#E8E8E4] mt-10 mb-4 leading-tight">
          {renderInline(line.slice(3))}
        </h2>
      );
    } else if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2));
    } else if (line.startsWith('*') && line.endsWith('*') && line.length > 2) {
      flushList();
      blocks.push(
        <p key={`em-${key++}`} className="text-sm text-[#888780] italic mt-6 mb-2">
          {renderInline(line.slice(1, -1))}
        </p>
      );
    } else if (line.length === 0) {
      flushList();
    } else {
      flushList();
      blocks.push(
        <p key={`p-${key++}`} className="text-base text-[#E8E8E4] leading-relaxed my-4">
          {renderInline(line)}
        </p>
      );
    }
  }
  flushList();
  return blocks;
};

const GuidaDinamicaPage = () => {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/guides/${slug}`)
      .then(r => {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok) throw new Error('fetch_error');
        return r.json();
      })
      .then(d => { setGuide(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="text-[#888780] text-center">Caricamento guida…</div>
      </div>
    );
  }
  if (error || !guide) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="guida-not-found">
        <Helmet>
          <title>Guida non trovata — SAPIENS</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-3xl font-semibold text-[#E8E8E4] mb-3">Guida non trovata</h1>
        <p className="text-[#888780] mb-6">La guida richiesta non esiste o è stata rimossa.</p>
        <Link to="/" className="text-[#C9A84C] underline">← Torna alla home</Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-3xl mx-auto px-4 py-12"
      data-testid={`guida-${slug}`}
    >
      <Helmet>
        <title>{`${guide.title} — SAPIENS`}</title>
        <meta name="description" content={guide.meta_description} />
        <meta property="og:title" content={guide.title} />
        <meta property="og:description" content={guide.meta_description} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://www.finsapiens.it/guide/${guide.slug}`} />
      </Helmet>

      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-[#888780] hover:text-[#C9A84C] mb-6"
        data-testid="guida-back"
      >
        <ChevronLeft className="w-4 h-4" /> Torna indietro
      </Link>

      {guide.category && (
        <span
          className="inline-block text-xs px-2 py-1 rounded mb-3"
          style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C' }}
        >
          {guide.category}
        </span>
      )}

      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#E8E8E4] mb-6 leading-tight"
        data-testid="guida-title"
      >
        {guide.title}
      </h1>

      <article className="prose-invert" data-testid="guida-content">
        {renderContent(guide.content || '')}
      </article>
    </motion.div>
  );
};

export default GuidaDinamicaPage;
